import { Injectable, OnInit } from '@angular/core';
import { PatternService } from './api/pattern.service';
import { ConfigService } from '../config.service';

export interface RiskLevel {
  ucs?: number,
  minRisk?: number,
  name?: string,
  background: string,
  color: string,
  rgb?: string  // don't know why we need this but let's declare it here to avoid breaking anything
}

export type RiskDistribution = Array<RiskLevel>;

@Injectable()
export class RiskService implements OnInit {
  public riskLevels: RiskDistribution = [
    { ucs: 0, minRisk: 4, name: 'VERY_HIGH', background: '#DB2C27', rgb: 'rgb(219,44,39)', color: '#FFFFFF' },
    { ucs: 0, minRisk: 3, name: 'HIGH', background: '#F6B84F', rgb: 'rgb(246,184,79)', color: '#FFFFFF' },
    { ucs: 0, minRisk: 2, name: 'AVERAGE', background: '#FFDB0C', rgb: 'rgb(255,219,12)', color: '#FFFFFF' },
    { ucs: 0, minRisk: 1, name: 'LOW', background: '#B9D568', rgb: 'rgb(185,213,104)', color: '#FFFFFF' },
    { ucs: 0, minRisk: 0, name: 'VERY_LOW', background: '#74BD4C', rgb: 'rgb(116,189,76)', color: '#FFFFFF' },
    { ucs: 0, minRisk: -1, name: 'MULTIPLE_PATTERNS_ABBR', background: '#666666', rgb: 'rgb(102, 102, 102)', color: '#FFFFFF' }
  ];

  public multiplePatterns: RiskLevel[] = [
    { background: '#71C6ED', rgb: 'rgb(113,198,237)', color: '#FFFFFF' },
    { background: '#A4ED84', rgb: 'rgb(164,237,132)', color: '#FFFFFF' },
    { background: '#F2DC6F', rgb: 'rgb(242,220,111)', color: '#FFFFFF' },
    { background: '#E58080', rgb: 'rgb(229,128,128)', color: '#FFFFFF' }
  ];

  private riskLevelMultiplePatterns: RiskLevel = { ucs: 0, minRisk: -1, color: '#FFFFFF', background: '#8F8F8F', name: 'MULTIPLE_PATTERNS_ABBR' };

  constructor(private patternService: PatternService) {}

  ngOnInit() {}

  getRiskLevel(risk) {
    for (let i = 0; i < this.riskLevels.length; i++) {
      if (risk >= this.riskLevels[i].minRisk) {
        return this.riskLevels[i];
      }
    }
  }

  getMultiplePatterns(patternCount) {
    if (!patternCount || patternCount <= 1) {
      return this.multiplePatterns[0];
    }
    if (patternCount >= 4) {
      return this.multiplePatterns[3];
    }
    return this.multiplePatterns[patternCount - 1];
  }

  getUcRiskColor(risk) {
    return this.getRiskLevel(risk).background;
  }

  getUcRiskText(risk, translations) {
    if (risk != null && risk !== undefined && translations) {
      for (let i = 0; i < this.riskLevels.length; i++) {
        if (risk >= this.riskLevels[i].minRisk) {
          const label = !ConfigService.app.riCashCollectionMode ? translations.RISK : translations.SCORE;
          return `${label}: ${translations[this.riskLevels[i].name.toUpperCase()]}`;
        }
      }
    }
  }

  translateRisk(risk, translations) {
    if (risk != null && risk !== undefined && translations) {
      for (let i = 0; i < this.riskLevels.length; i++) {
        if (risk >= this.riskLevels[i].minRisk) {
          return `${translations[this.riskLevels[i].name.toUpperCase()]}`;
        }
      }
    }
  }

  riskDistribution(ucs: any[]): any[] {
    const result = this.riskLevels.map(level => Object.assign({}, level, { ucs: 0 }));
    const multiplePatternsLevel = result.find(x => x.name === 'MULTIPLE_PATTERNS_ABBR');

    for (const uc of ucs) {
      if (uc.patterns && uc.patterns.length > 1) {
        multiplePatternsLevel.ucs++;
      } else {
        for (let k = 0; k < this.riskLevels.length; k++) {
          if (uc.patterns && uc.patterns[0].risk >= this.riskLevels[k].minRisk) {
            result[k].ucs++;
            break;
          }
        }
      }
    }
    return result;
  }


  riskDistributionFromData(patterns: any[], patternResultFilter: string): RiskDistribution {
    const result = this.riskLevels.map(level => Object.assign({}, level, { ucs: 0 }));
    if (!patterns) {
      return result;
    }
    const multiplePatternsLevel = result.find(x => x.name === 'MULTIPLE_PATTERNS_ABBR');
    result.forEach(level => {
      patterns.forEach(pattern => {
        switch (level.name) {
          case 'VERY_HIGH':
            level.ucs += !isNaN(pattern.ucsVeryHigh) ? pattern.ucsVeryHigh : 0;
            break;
          case 'HIGH':
            level.ucs += !isNaN(pattern.ucsHigh) ? pattern.ucsHigh : 0;
            break;
          case 'AVERAGE':
            level.ucs += !isNaN(pattern.ucsMedium) ? pattern.ucsMedium : 0;
            break;
          case 'LOW':
            level.ucs += !isNaN(pattern.ucsLow) ? pattern.ucsLow : 0;
            break;
          case 'VERY_LOW':
            level.ucs += !isNaN(pattern.ucsVeryLow) ? pattern.ucsVeryLow : 0;
            break;
        }
      });
    });
    // this.patternService.getUCsQtyMultiplePatterns('', patternResultFilter).subscribe(data => {
    //   multiplePatternsLevel.ucs = data;
    // });
    return result;
  }

  riskDistributionFromFilter(ucFilter: string, patternResultFilter: string): RiskDistribution {
    const result = this.riskLevels.map(level => Object.assign({}, level, { ucs: 0 }));
    const multiplePatternsLevel = result.find(x => x.name === 'MULTIPLE_PATTERNS_ABBR');

    this.patternService.getUCsQtyByRiskRange(ucFilter, patternResultFilter).subscribe(data => {
      Object.keys(data).forEach(computedLevelName => {
        const level = result.find(x => x.name === computedLevelName.toUpperCase());
        if (level) {
          level.ucs = data[computedLevelName];
        }
      });
    });
    this.patternService.getUCsQtyMultiplePatterns(ucFilter, patternResultFilter).subscribe(data => {
      multiplePatternsLevel.ucs = data;
    });
    return result;
  }

  mergeDistributions(distributions: RiskDistribution[]): RiskDistribution {
    const result = this.riskLevels.map(level => Object.assign({}, level, { ucs: 0 }));
    return distributions.reduce((acc, d) => {
      for (let i = 0; i < acc.length; i++) {
        acc[i].ucs += d[i].ucs;
      }
      return acc;
    }, result);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/risk.service.ts